import React, { useEffect, useRef, useState, useMemo } from "react";
import * as d3 from "d3";
import { fetchAPI } from "../../../config/api";
import { 
  computeAggregates, 
  getHeatColor 
} from "../../../utils/sprayChartUtils";

const drawInfield = (field, { centerX, centerY, infieldRadius, infieldZoneData, isCompact = false }) => {
  const pie = d3.pie()
    .startAngle(-0.785398)
    .endAngle(0.785398)
    .value(() => 1)
    .sort(null);

  const arc = d3.arc().innerRadius(0).outerRadius(infieldRadius);
  const slices = pie([1, 1, 1, 1, 1]);

  slices.forEach((slice, i) => {
    const zone = infieldZoneData[i];
    const percentage = zone?.percentage || 0;
    const fillColor = getHeatColor(percentage, 'infield');

    field.append("path")
      .attr("d", arc(slice))
      .attr("transform", `translate(${centerX},${centerY})`)
      .attr("fill", fillColor)
      .attr("stroke", "#333")
      .attr("stroke-width", 0.5)
      .attr("opacity", 1);

    // Add percentage label (skip for compact view - too small)
    if (!isCompact && percentage > 0) {
      const angle = (slice.startAngle + slice.endAngle) / 2;
      const textR = infieldRadius * 0.55;
      const textX = centerX + Math.sin(angle) * textR;
      const textY = centerY - Math.cos(angle) * textR;

      field.append("text")
        .attr("x", textX)
        .attr("y", textY)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("font-weight", "bold")
        .attr("font-size", "7px")
        .attr("fill", "#1a202c")
        .text(`${percentage}%`);
    }
  });
};

const drawOutfield = (field, { centerX, centerY, arcRadius, infieldRadius, outfieldZoneData, isCompact = false }) => {
  const pie = d3.pie()
    .startAngle(-0.785398)
    .endAngle(0.785398)
    .value(() => 1)
    .sort(null);

  const arc = d3.arc().innerRadius(infieldRadius).outerRadius(arcRadius);
  const slices = pie([1, 1, 1]);

  slices.forEach((slice, i) => {
    const zone = outfieldZoneData[i];
    const percentage = zone?.percentage || 0;
    const hrCount = zone?.hrCount || 0;
    const fillColor = getHeatColor(percentage, 'outfield');

    field.append("path")
      .attr("d", arc(slice))
      .attr("transform", `translate(${centerX},${centerY})`)
      .attr("fill", fillColor)
      .attr("stroke", "#333")
      .attr("stroke-width", 1)
      .attr("opacity", 0.95);

    const angle = (slice.startAngle + slice.endAngle) / 2;

    // Add percentage label in the zone
    const textR = (infieldRadius + arcRadius) / 2;
    const textX = centerX + Math.sin(angle) * textR;
    const textY = centerY - Math.cos(angle) * textR;
    const fontSize = isCompact ? "8px" : "11px";

    field.append("text")
      .attr("x", textX)
      .attr("y", textY)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("font-weight", "bold")
      .attr("font-size", fontSize)
      .attr("fill", "#1a202c")
      .text(`${percentage}%`);

    // HR count circles
    if (hrCount > 0) {
      const hrRadius = arcRadius * 1.12;
      const hrX = centerX + Math.sin(angle) * hrRadius;
      const hrY = centerY - Math.cos(angle) * hrRadius;
      const circleR = isCompact ? 6 : 8;

      field.append("circle")
        .attr("cx", hrX)
        .attr("cy", hrY)
        .attr("r", circleR)
        .attr("fill", "#E1F5FE")
        .attr("stroke", "#0D47A1")
        .attr("stroke-width", 1.5)
        .attr("opacity", 1);

      field.append("text")
        .attr("x", hrX)
        .attr("y", hrY)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("font-weight", "bold")
        .attr("font-size", isCompact ? "6px" : "8px")
        .attr("fill", "#0D47A1")
        .text(hrCount);
    }
  });
};

export const SprayChartMini = ({ playerId, year, division, prefetchedData, guts, width = 200, height = 200, handFilter = { L: true, R: true }, label = "Spray Chart" }) => {
  const svgRef = useRef();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!playerId) return;

    if (prefetchedData?.events) {
      setEvents(Array.isArray(prefetchedData.events) ? prefetchedData.events : []);
      return;
    }

    const fetchData = async () => {
      try {
        const data = await fetchAPI(
          `/spraychart_data/${playerId}?year=${year}&division=${division}`
        );
        setEvents(Array.isArray(data.events) ? data.events : []);
      } catch (err) {
        console.warn("Could not fetch spray chart data:", err);
      }
    };

    fetchData();
  }, [playerId, year, division, prefetchedData]);

  const aggregates = useMemo(() => computeAggregates(events, { handFilter, guts }), [events, handFilter, guts]);

  useEffect(() => {
    if (!svgRef.current || !aggregates) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("viewBox", `0 0 ${width} ${height}`);

    const { outfieldZones: outfieldZoneData, infieldZones: infieldZoneData } = aggregates;

    const margin = { top: 0, right: 5, bottom: 0, left: 5 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const field = svg.append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const centerX = innerWidth / 2;
    const centerY = innerHeight * 0.7;
    const arcRadius = Math.min(innerWidth * 0.5, innerHeight * 0.72);
    const infieldRadius = arcRadius * 0.52;

    const isCompact = width < 180;
    drawInfield(field, { centerX, centerY, infieldRadius, infieldZoneData, isCompact });
    drawOutfield(field, { centerX, centerY, arcRadius, infieldRadius, outfieldZoneData, isCompact });
  }, [aggregates, width, height]);

  if (events.length === 0) return null;

  return (
    <div className="flex flex-col items-center">
      <span className="text-[10px] text-gray-500 mb-1 font-medium">{label}</span>
      <svg ref={svgRef} width={width} height={height} />
    </div>
  );
};
